import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from './auth.service';
import { CryptoService } from './crypto.service';

/** Requires an authenticated session; tries to restore one before redirecting. */
export const authGuard: CanActivateFn = async (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (auth.isAuthenticated()) return true;
  if (await auth.tryRestore()) return true;

  // Preserve the intended destination (e.g. a scanned QR deep link) across login.
  return router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url } });
};

/** Requires the vault to be unlocked (DEK in memory); otherwise routes to unlock. */
export const unlockedGuard: CanActivateFn = async () => {
  const cryptoService = inject(CryptoService);
  const auth = inject(AuthService);
  const router = inject(Router);

  // Guards on the same route run concurrently, so on a page refresh the
  // session restore kicked off by authGuard may still be in flight — await it
  // (tryRestore is single-flight) instead of redirecting to login prematurely.
  if (!auth.isAuthenticated() && !(await auth.tryRestore())) {
    return router.createUrlTree(['/login']);
  }
  if (cryptoService.unlocked) return true;
  // Survive a page refresh: re-import the DEK cached for this tab session.
  if (await cryptoService.restore()) return true;
  return router.createUrlTree(['/unlock']);
};
